import { createContext, useCallback, useContext, lazy, Suspense, useState, type ReactNode } from 'react';
import type { FlowIntent } from '../home/studio/types';
import type { UpskillPlanResponse } from '../../types/api.types';
import { useNavigateWithParams } from '../../hooks/useNavigateWithParams';
import { StudioLaunchFlow } from '../studio/StudioLaunchFlow';

const CreatePlanWidget = lazy(() => import('../CreatePlanWidget'));

interface GetStartedFlowApi {
  /** Open the get-started overlay — plan uses CreatePlanWidget; agent opens the Studio launch modal. */
  open: (intent: FlowIntent) => void;
  close: () => void;
}

const GetStartedFlowContext = createContext<GetStartedFlowApi | null>(null);

/** Open/close the unified get-started overlay from any CTA (Mock 5). */
export function useGetStartedFlow(): GetStartedFlowApi {
  const ctx = useContext(GetStartedFlowContext);
  if (!ctx) throw new Error('useGetStartedFlow must be used within <GetStartedFlowProvider>');
  return ctx;
}

/** Mounts once near the app root; renders the overlay when open. */
export function GetStartedFlowProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigateWithParams();
  const [intent, setIntent] = useState<FlowIntent | null>(null);
  const open = useCallback((next: FlowIntent) => setIntent(next), []);
  const close = useCallback(() => setIntent(null), []);

  const onPlanCreated = useCallback(
    (plan: UpskillPlanResponse) => {
      close();
      navigate('/upskill-plan', { state: { plan } });
    },
    [close, navigate],
  );

  return (
    <GetStartedFlowContext.Provider value={{ open, close }}>
      {children}
      {intent === 'agent' && <StudioLaunchFlow onClose={close} />}
      {intent === 'plan' && (
        <Suspense fallback={null}>
          <CreatePlanWidget isOpen onClose={close} onPlanCreated={onPlanCreated} />
        </Suspense>
      )}
    </GetStartedFlowContext.Provider>
  );
}
